import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { jwtDecode } from 'jwt-decode'; // Install this library using `npm install jwt-decode`
import './LocationDropdown.css';

const LocationDropDown = () => {
    const [toLocations, setToLocations] = useState([]);
    const [selectedToLocation, setSelectedToLocation] = useState(null);
    const [results, setResults] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState('');
    const [jwtToken, setJwtToken] = useState(null);
    const [clientCredentials, setClientCredentials] = useState({ client_id: '', client_secret: '' });
    const recordsPerPage = 10;
    let client_id = '';
    let client_secret = '';

    // Function to fetch client credentials
    const fetchClientCredentials = async () => {
        try {
            const response = await fetch(process.env.REACT_APP_GET_CLIENT_CREDENTIALS_URL);
            if (response.ok) {
                const data = await response.json();
                client_id = data.clientId;
                client_secret= data.clientSecret;
            } else {
                console.error('Failed to fetch client credentials');
            }
        } catch (error) {
            console.error('Error fetching client credentials:', error);
        }
    };

    // Function to fetch JWT token using client credentials
    const fetchJwtToken = async () => {
        try {
            const response = await fetch(process.env.REACT_APP_AUTH_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    grant_type: 'client_credentials',
                    client_id: client_id,
                    client_secret: client_secret,
                    scope: process.env.REACT_APP_API_SCOPE,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                setJwtToken(data.access_token);
            } else {
                console.error('Failed to fetch JWT token');
            }
        } catch (error) {
            console.error('Error fetching JWT token:', error);
        }
    };

    // Function to check if the token is expired
    const isTokenExpired = (token) => {
        try {
            const decoded = jwtDecode(token);
            const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
            return decoded.exp < currentTime;
        } catch (error) {
            console.error('Error decoding token:', error);
            return true; // Treat as expired if decoding fails
        }
    };

    // Function to ensure a valid token is available
    const ensureValidToken = async () => {
        if (!jwtToken || isTokenExpired(jwtToken)) {
        if(!clientCredentials.client_id || !clientCredentials.client_secret) {
        fetchClientCredentials()
            .then(() => fetchJwtToken())
            .catch((error) => console.error('Error during initialization:', error));
        }else{
         await fetchJwtToken();
        }
        }
    };

    useEffect(() => {
        const fetchLocations = async () => {
            await ensureValidToken();
            if (jwtToken) {
                fetch(process.env.REACT_APP_API_URL + '/getShipmentToDropDownList', {
                    cache: 'no-store',
                    headers: {
                        Authorization: `Bearer ${jwtToken}`,
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => response.json())
                    .then((data) =>
                        setToLocations(data.map((location) => ({ value: location, label: location })))
                    )
                    .catch((error) => console.error('Error fetching to locations:', error));
            }
        };

        fetchLocations();
    }, [jwtToken]);

    const handleToChange = (selectedOption) => {
        setSelectedToLocation(selectedOption);
        const [shipFromIdCode, shipFromName] = selectedOption.value.split(' - ');
        fetchDataForLocation(selectedOption.value, shipFromIdCode, shipFromName);
    };

    const fetchDataForLocation = async (locationId, shipFromIdCode, shipFromName) => {
        await ensureValidToken();
        if (locationId !== 'Select a location' && jwtToken) {
            const encodedShipFromName = encodeURIComponent(shipFromName);
            fetch(
                process.env.REACT_APP_API_URL +
                    '/getErrorsByShipToAddressId?identificationCode=' +
                    shipFromIdCode +
                    '&name=' +
                    encodedShipFromName,
                {
                    cache: 'no-store',
                    headers: {
                        Authorization: `Bearer ${jwtToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            )
                .then((response) => response.json())
                .then((data) => {
                    const updatedData = data.map((record) => ({
                        ...record,
                        actionStatus: 'Pending',
                        isSubmitEnabled: false,
                    }));
                    setResults(updatedData);
                    setCurrentPage(1); // Reset to first page on new search
                })
                .catch((error) => console.error('Error fetching failed EDI records:', error));
        } else {
            setResults([]);
        }
    };

    const handleActionChange = (event, recordId) => {
        const newActionStatus = event.target.value;
        setResults((prevResults) =>
            prevResults.map((record) =>
                record.ediObjectId === recordId
                    ? { ...record, actionStatus: newActionStatus, isSubmitEnabled: newActionStatus === 'Verified' }
                    : record
            )
        );
    };

    const handleSubmit = async (recordId) => {
        await ensureValidToken();
        const record = results.find((record) => record.ediObjectId === recordId);
        if (record && selectedToLocation && jwtToken) {
            const selectedLocation = selectedToLocation.value;
            const [shipFromIdCode, shipFromName] = selectedLocation.split(' - ');
            const encodedShipFromName = encodeURIComponent(shipFromName);
            fetch(
                process.env.REACT_APP_API_URL +
                    '/updateActionStatus?id=' +
                    recordId +
                    '&identificationCode=' +
                    shipFromIdCode +
                    '&name=' +
                    encodedShipFromName,
                {
                    cache: 'no-store',
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${jwtToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            )
                .then((response) => {
                    if (response.ok) {
                        console.log('Edi record verified successfully');
                        alert('Edi record verified successfully');
                        fetchDataForLocation(selectedToLocation.value, shipFromIdCode, shipFromName); // Refresh data for the selected location
                    } else {
                        console.error('Failed to update action status');
                        alert('Failed to update action verified. Please try again later.');
                    }
                })
                .catch((error) => {
                    alert('Error while updating the action as verified: ' + error.message);
                    console.error('Error while updating the action as verified:', error);
                });
        }
    };

    const handleFileNameClick = (content) => {
        setModalContent(content);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setModalContent('');
    };

    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = results.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(results.length / recordsPerPage);

    const handleNextPage = () => {
        setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages));
    };

    const handlePreviousPage = () => {
        setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
    };

    return (
        <div className="dropdown-container">
            <header className="page-header">Failed EDI Records Viewer</header>
            <div className="dropdown-row">
                <div className="dropdown-item">
                    <label htmlFor="toLocation">Ship To Location:</label>
                    <Select
                        id="toLocation"
                        value={selectedToLocation}
                        onChange={handleToChange}
                        options={toLocations}
                        placeholder="Select a location"
                        className="react-select-container"
                    />
                </div>
            </div>

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th>EDI Object ID</th>
                            <th>Wholesaler</th>
                            <th>Ship From Location</th>
                            <th>EDI File Name</th>
                            <th>Error Description From Tracelink</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentRecords.length ? (
                            currentRecords.map((record) => (
                                <tr key={record.ediObjectId}>
                                    <td>{record.ediObjectId}</td>
                                    <td>{record.wholesalerName}</td>
                                    <td>
                                        {record.shipFromIdCode} - {record.shipFromName}
                                    </td>
                                    <td>
                                        <a href="#" onClick={() => handleFileNameClick(record.ediContent)}>
                                            {record.ediFilename}
                                        </a>
                                    </td>
                                    <td>{record.errorDesc}</td>
                                    <td>
                                        <select
                                            value={record.actionStatus}
                                            onChange={(e) => handleActionChange(e, record.ediObjectId)}
                                        >
                                            <option value="Pending">Pending</option>
                                            <option value="Verified">Verified</option>
                                        </select>
                                    </td>
                                    <td>
                                        <button
                                            onClick={() => handleSubmit(record.ediObjectId)}
                                            disabled={!record.isSubmitEnabled}
                                        >
                                            Submit
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className="no-records">
                                    No Failed Edi Documents found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {results.length > 0 && (
                    <div className="pagination">
                        <button onClick={handlePreviousPage} disabled={currentPage === 1}>
                            Previous
                        </button>
                        <button onClick={handleNextPage} disabled={currentPage === totalPages}>
                            Next
                        </button>
                    </div>
                )}
            </div>

            {isModalOpen && (
                <div className="modal">
                    <div className="modal-content">
                        <span className="close" onClick={closeModal}>
                            &times;
                        </span>
                        <pre>{modalContent}</pre>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LocationDropDown;